import { useEffect, useId, useRef, useState } from 'react';
import type { FormatoExportacion } from '@cmms/shared';
import { consulta, descargarArchivo } from '../servicios/api.js';
import { notificarErrorGlobal } from './notificaciones-globales.js';

const ETIQUETAS: Record<FormatoExportacion, string> = {
  xlsx: 'Excel (.xlsx)',
  csv: 'CSV (.csv)',
  pdf: 'PDF (.pdf)',
};

interface Propiedades {
  /** Ruta base del módulo, p. ej. `/activos`. La exportación vive en `${ruta}/exportar/:formato`. */
  ruta: string;
  /** Los mismos filtros del listado que está en pantalla, sin paginación. */
  filtros: Record<string, unknown>;
}

/**
 * Botón «Exportar» con menú desplegable. Exporta lo que el usuario está viendo
 * (mismos filtros), no solo la página actual. Se cierra con Escape, con clic
 * fuera o al elegir un formato, y devuelve el foco al botón.
 */
export function BotonExportar({ ruta, filtros }: Propiedades): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [enCurso, setEnCurso] = useState<FormatoExportacion | null>(null);
  const contenedor = useRef<HTMLDivElement>(null);
  const disparador = useRef<HTMLButtonElement>(null);
  const idMenu = useId();

  useEffect(() => {
    if (!abierto) return;
    const alPulsar = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAbierto(false);
        disparador.current?.focus();
      }
    };
    document.addEventListener('mousedown', alPulsar);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', alPulsar);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierto]);

  async function exportar(formato: FormatoExportacion): Promise<void> {
    setAbierto(false);
    setEnCurso(formato);
    try {
      await descargarArchivo(`${ruta}/exportar/${formato}${consulta(filtros)}`);
    } catch (error) {
      notificarErrorGlobal(error instanceof Error ? error.message : 'No se pudo exportar.');
    } finally {
      setEnCurso(null);
    }
  }

  return (
    <div className="menu-desplegable" ref={contenedor}>
      <button
        ref={disparador}
        type="button"
        className="boton secundario"
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-controls={idMenu}
        aria-busy={enCurso !== null}
        disabled={enCurso !== null}
        onClick={() => setAbierto((v) => !v)}
      >
        {enCurso ? 'Exportando…' : 'Exportar'}
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className={abierto ? 'flecha-abierta' : ''}>
          <path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {abierto ? (
        <div id={idMenu} className="menu-opciones-plantilla" role="menu" aria-label="Formato de exportación">
          {(Object.keys(ETIQUETAS) as FormatoExportacion[]).map((formato) => (
            <button key={formato} type="button" role="menuitem" onClick={() => void exportar(formato)}>
              {ETIQUETAS[formato]}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
